import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import LevelBadge from "@/components/level/LevelBadge";
import { Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function RankingList() {
  const { organization } = useOrganization();
  const navigate = useNavigate();

  const { data: players = [], isLoading } = useQuery({
    queryKey: ["ranking-list", organization?.id],
    queryFn: async () => {
      // Get all org members
      const { data: members } = await supabase
        .from("organization_members")
        .select("user_id")
        .eq("organization_id", organization!.id);

      const userIds = (members ?? []).map((m: Record<string, unknown>) => m.user_id);
      if (userIds.length === 0) return [];

      // Get profiles via public_profiles view (Sprint 1 Phase 3 — column-restricted PII closure)
      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level, avatar_url")
        .in("id", userIds)
        .order("level", { ascending: false });

      // Get match stats from player_stats_view
      const { data: stats } = await supabase
        .from("player_stats_view")
        .select("player_id, total_matches, wins, win_rate")
        .eq("organization_id", organization!.id);

      const statsMap = new Map((stats ?? []).map((s: Record<string, unknown>) => [s.player_id, s]));

      // Combine and sort: level desc, then wins desc
      const combined = (profiles ?? []).map((p: { id: string; full_name: string | null; level: number | null; avatar_url: string | null }) => ({
        ...p,
        stats: statsMap.get(p.id) ?? { total_matches: 0, wins: 0, win_rate: 0 },
      }));

      // PR-AY2b (mechanical apply of PR-AY2a Tables<> pattern): the combined
      // array's element type is already inferred from the .map() above
      // (profile shape + stats shape). Replacing `Record<string, unknown>`
      // with the proper inferred type lets TS see `b.stats.wins` as
      // `number` instead of `unknown`.
      type CombinedPlayer = (typeof combined)[number];
      combined.sort((a: CombinedPlayer, b: CombinedPlayer) => {
        const levelDiff = (b.level ?? 0) - (a.level ?? 0);
        if (levelDiff !== 0) return levelDiff;
        return (b.stats.wins ?? 0) - (a.stats.wins ?? 0);
      });

      return combined;
    },
    enabled: !!organization,
  });

  if (isLoading) {
    return <div className="space-y-2">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }

  if (players.length === 0) {
    return <p className="text-center text-muted-foreground py-12">Inga spelare registrerade</p>;
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="grid grid-cols-[2rem_1fr_auto_3.5rem_3.5rem] gap-2 px-3 py-1.5 text-xs text-muted-foreground font-medium">
        <span>#</span>
        <span>Spelare</span>
        <span />
        <span className="text-center">Matcher</span>
        <span className="text-center">V%</span>
      </div>

      {players.map((player: { id: string; full_name: string | null; level: number | null; stats: { total_matches: number; wins: number; win_rate: number } }, index: number) => (
        <Card
          key={player.id}
          className="cursor-pointer hover:border-primary/40 transition-colors"
          onClick={() => navigate(`/players/${player.id}`)}
        >
          <CardContent className="p-3 grid grid-cols-[2rem_1fr_auto_3.5rem_3.5rem] gap-2 items-center">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
              {index < 3 ? (
                <Trophy className={`h-3.5 w-3.5 ${
                  index === 0 ? "text-yellow-500" : index === 1 ? "text-gray-400" : "text-amber-700"
                }`} />
              ) : (
                index + 1
              )}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="text-xs">
                  {(player.full_name?.[0] ?? "?").toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <p className="font-medium text-sm truncate">{player.full_name || "Okänd"}</p>
            </div>
            <LevelBadge level={player.level ?? 1} size="sm" />
            <span className="text-xs text-center tabular-nums">{player.stats.total_matches ?? 0}</span>
            <span className="text-xs text-center tabular-nums font-medium">
              {player.stats.total_matches > 0 ? `${Math.round((player.stats.win_rate ?? 0) * 100)}%` : "—"}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
